import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { User, Phone, Mail, MapPin, FileText, Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SmsConsentCheckbox } from "@/components/SmsConsentCheckbox";

const OWNERSHIP_TYPES = ["Owner", "Tenant", "Property Manager", "Authorized Representative"];
const PAYMENT_TYPES = ["Insurance", "Self Pay"];

export default function Step2CustomerInfo({ workflow, request, requestId, onAdvance }) {
  const [saving, setSaving] = useState(false);
  const initial = workflow?.step_2 || {};
  const [form, setForm] = useState({
    full_name: initial.full_name || request?.customer_name || "",
    phone: initial.phone || request?.customer_phone || "",
    email: initial.email || request?.customer_email || "",
    address_line1: initial.address_line1 || request?.property_address_line1 || "",
    city: initial.city || request?.property_city || "",
    state: initial.state || request?.property_state || "",
    zip_code: initial.zip_code || request?.property_zip_code || "",
    ownership_type: initial.ownership_type || "Owner",
    authorizing_party_name: initial.authorizing_party_name || "",
    payment_type: initial.payment_type || "Insurance",
    insurance_company: initial.insurance_company || "",
    claim_number: initial.claim_number || "",
    adjuster_name: initial.adjuster_name || "",
    adjuster_phone: initial.adjuster_phone || "",
    sms_opt_in: initial.sms_opt_in ?? !!request?.customer_sms_opt_in,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const showAuthorizingParty = form.ownership_type !== "Owner";
  const showInsuranceFields = form.payment_type === "Insurance";

  const submit = async () => {
    if (!form.full_name.trim()) return toast.error("Customer name is required");
    if (showAuthorizingParty && !form.authorizing_party_name.trim()) {
      return toast.error("Authorizing party name is required for non-owners");
    }
    if (showInsuranceFields) {
      if (!form.insurance_company.trim()) return toast.error("Insurance company is required");
      if (!form.claim_number.trim()) return toast.error("Claim number is required");
    }

    setSaving(true);
    try {
      await API.post(`/inspection/${requestId}/step/2/save`, form);
      toast.success("Customer info confirmed");
      onAdvance(3);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="step-2-form">
      <div className="flex items-center gap-2">
        <User className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 2 · Customer Information</h2>
      </div>

      <Card><CardContent className="pt-4 space-y-3">
        <FieldGroup label="Full Name" icon={<User className="w-3.5 h-3.5" />}>
          <Input value={form.full_name} onChange={e => set("full_name", e.target.value)} className="h-11" data-testid="step-2-full-name" />
        </FieldGroup>
        <div className="grid grid-cols-2 gap-2">
          <FieldGroup label="Phone" icon={<Phone className="w-3.5 h-3.5" />}>
            <Input value={form.phone} onChange={e => set("phone", e.target.value)} className="h-11" data-testid="step-2-phone" />
          </FieldGroup>
          <FieldGroup label="Email" icon={<Mail className="w-3.5 h-3.5" />}>
            <Input value={form.email} onChange={e => set("email", e.target.value)} className="h-11" data-testid="step-2-email" />
          </FieldGroup>
        </div>
        {form.phone && (
          <SmsConsentCheckbox checked={form.sms_opt_in} onChange={v => set("sms_opt_in", v)} testId="step-2-sms-opt-in" />
        )}
        <FieldGroup label="Address" icon={<MapPin className="w-3.5 h-3.5" />}>
          <Input value={form.address_line1} onChange={e => set("address_line1", e.target.value)} className="h-11" data-testid="step-2-address" />
        </FieldGroup>
        <div className="grid grid-cols-3 gap-2">
          <Input value={form.city} onChange={e => set("city", e.target.value)} placeholder="City" className="h-11" />
          <Input value={form.state} onChange={e => set("state", e.target.value)} placeholder="State" className="h-11" />
          <Input value={form.zip_code} onChange={e => set("zip_code", e.target.value)} placeholder="ZIP" className="h-11" />
        </div>
      </CardContent></Card>

      <Card><CardContent className="pt-4 space-y-3">
        <FieldGroup label="Ownership Type" icon={<Shield className="w-3.5 h-3.5" />}>
          <Select value={form.ownership_type} onValueChange={v => set("ownership_type", v)}>
            <SelectTrigger className="h-11" data-testid="step-2-ownership-select"><SelectValue /></SelectTrigger>
            <SelectContent>{OWNERSHIP_TYPES.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </FieldGroup>
        {showAuthorizingParty && (
          <FieldGroup label="Authorizing Party Name" required>
            <Input value={form.authorizing_party_name}
                   onChange={e => set("authorizing_party_name", e.target.value)}
                   className="h-11" data-testid="step-2-authorizing-party" />
          </FieldGroup>
        )}
      </CardContent></Card>

      <Card><CardContent className="pt-4 space-y-3">
        <FieldGroup label="Payment Type" icon={<FileText className="w-3.5 h-3.5" />}>
          <div className="grid grid-cols-2 gap-2">
            {PAYMENT_TYPES.map(p => (
              <Button key={p} type="button" variant={form.payment_type === p ? "default" : "outline"}
                      onClick={() => set("payment_type", p)} className="h-12"
                      data-testid={`step-2-payment-${p.toLowerCase().replace(" ", "-")}`}>
                {p}
              </Button>
            ))}
          </div>
        </FieldGroup>
        {showInsuranceFields && (
          <div className="space-y-3 pt-2 border-t border-zinc-100">
            <FieldGroup label="Insurance Company" required>
              <Input value={form.insurance_company} onChange={e => set("insurance_company", e.target.value)} className="h-11" data-testid="step-2-insurance-company" />
            </FieldGroup>
            <FieldGroup label="Claim Number" required>
              <Input value={form.claim_number} onChange={e => set("claim_number", e.target.value)} className="h-11" data-testid="step-2-claim-number" />
            </FieldGroup>
            <div className="grid grid-cols-2 gap-2">
              <FieldGroup label="Adjuster Name">
                <Input value={form.adjuster_name} onChange={e => set("adjuster_name", e.target.value)} className="h-11" data-testid="step-2-adjuster-name" />
              </FieldGroup>
              <FieldGroup label="Adjuster Phone">
                <Input value={form.adjuster_phone} onChange={e => set("adjuster_phone", e.target.value)} className="h-11" data-testid="step-2-adjuster-phone" />
              </FieldGroup>
            </div>
          </div>
        )}
      </CardContent></Card>

      <Button onClick={submit} disabled={saving}
              className="w-full h-12 bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="step-2-save-btn">
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Confirm & Continue"}
      </Button>
    </div>
  );
}

function FieldGroup({ label, icon, required, children }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-1">
        {icon}{label}{required && <span className="text-rose-500">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
